import { RestManager } from '@/newApi/services/rest/RestManager';
import { getApiHost } from '@/appSettings';
import type { CreateIndividualBudgetParams, BudgetGroup, IndividualBudget, IndividualBudgetKeyValues, IndividualBudgetSummaryKeyValues, IndividualBudgetTableData } from '@sysarb/sysarb-types/services/v1/pay-management';
import type { Guid } from 'guid-typescript';
import { Role } from '@sysarb/sysarb-types/service-base/enums';
import type { useResgate } from '@/login/composables/useResgate';
import { useLoginStore } from '@/login/stores/useLoginStore';
import type { SalaryRaiseColumn } from '@sysarb/sysarb-types/services/v1/pay-management/enums';

const payManagementService = new RestManager(`https://${getApiHost()}/pay-management-service`);

export function abort(): void {
    payManagementService.abort();
}

/**
 * Get budgets by salarycolumn
 *  @param reviewId review id
 *  @param salaryRaiseColumn salaryRaiseColumn on the budget
 */
export async function getIndividualBudgetsCollection(resgate: ReturnType<typeof useResgate>, reviewId: string, salaryRaiseColumn: SalaryRaiseColumn): Promise<Iterable<IndividualBudget> | null> {
    const promise = resgate.collection<IndividualBudget>(`v1.pay-management.individual-budget.${reviewId}.getbyreviewid.${salaryRaiseColumn}`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Update budget
 *  @param budgetId budget id
 */
export async function updateIndividualBudget(resgate: ReturnType<typeof useResgate>, budget: IndividualBudget): Promise<null> {
    const payload: IndividualBudget = {
        id: budget.id,
        createdAt: budget.createdAt,
        updatedAt: budget.updatedAt,
        name: budget.name,
        description: budget.description,
        generalDone: budget.generalDone,
        selectionDone: budget.selectionDone,
        calculateDone: budget.calculateDone,
        filter: budget.filter,
        reviewId: budget.reviewId,
        salaryBudgetAppliesTo: budget.salaryBudgetAppliesTo,
        budgetType: budget.budgetType,
        budgetSalaryType: budget.budgetSalaryType,
        budgetValue: budget.budgetValue
    };

    return resgate.call<null>(`v1.pay-management.individual-budget.${budget.id}`, 'update', payload).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
}

/**
 * Get budget by review id
 *  @param reviewId review id
 */
export async function getIndividualBudgetByReviewId(resgate: ReturnType<typeof useResgate>, reviewId: string): Promise<IndividualBudget | null> {
    const promise = resgate.model<IndividualBudget>(`v1.pay-management.individual-budget.${reviewId}.getbyreviewid`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Get budget by id
 *  @param budgetId budget id
 */
export async function getIndividualBudgetById(resgate: ReturnType<typeof useResgate>, budgetId: string): Promise<IndividualBudget | null> {
    const promise = resgate.model<IndividualBudget>(`v1.pay-management.individual-budget.${budgetId}`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Delete budget
 *  @param budgetId budget id
 */
export async function deleteIndividualBudget(resgate: ReturnType<typeof useResgate>, budgetId: string): Promise<IndividualBudget | null> {
    const promise = resgate.call<IndividualBudget>(`v1.pay-management.individual-budget.${budgetId}`, 'delete', {}).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Create individual budget
 *  @param reviewId review id
 *  @param salaryRaiseColumn salaryRaiseColumn on the budget
 */
export async function createIndividualBudget(resgate: ReturnType<typeof useResgate>, reviewId: string, salaryRaiseColumn: SalaryRaiseColumn): Promise<IndividualBudget | null> {
    const payload: CreateIndividualBudgetParams = {
        reviewId,
        salaryBudgetAppliesTo: salaryRaiseColumn,
    };
    const promise = resgate.call<IndividualBudget>('v1.pay-management.individual-budget', 'create', payload).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Fetch all employments from a reviewDraft id.
 * @param individualBudgetId Id of the pay management review
 * @returns employments filtered on the pay management review
 */
export async function getIndividualBudgetEmployments(resgate: ReturnType<typeof useResgate>, individualBudgetId: string): Promise<IndividualBudgetTableData | null> {
    const { instance } = useLoginStore();
    return payManagementService.post<IndividualBudgetTableData | null>({}, `v1/${instance}/pay-management/review-employments/${individualBudgetId}/individualbudgettabledata`);
}

/**
 * Get key values per budget
 *  @param reviewId review id
 */
export async function getIndividualBudgetKeyValues(resgate: ReturnType<typeof useResgate>, individualBudgetId: string): Promise<IndividualBudgetKeyValues | null> {
    const { instanceTopic } = useLoginStore();
    const promise = resgate.model<IndividualBudgetKeyValues>(`v1.pay-management.subdomain.${instanceTopic}.individual-budget.${individualBudgetId}.keyvalues`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Get summary key values for individual budgets
 *  @param reviewId review id
 */
export async function getIndividualBudgetSummaryKeyValues(resgate: ReturnType<typeof useResgate>, reviewId: string): Promise<IndividualBudgetSummaryKeyValues | null> {
    const { instanceTopic } = useLoginStore();
    const promise = resgate.model<IndividualBudgetSummaryKeyValues>(`v1.pay-management.subdomain.${instanceTopic}.individual-budget.${reviewId}.summarykeyvalues`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Get individual budgets for manager and section
 *  @param salaryReviewId salary review id
 *  @param currentRole current user role
 *  @param currentOrganisationId current organisation id
 *  @param componentId component id
 */
export async function individualBudgetValues(resgate: ReturnType<typeof useResgate>, salaryReviewId: Guid, currentRole: Role, currentOrganisationId: string): Promise<Iterable<BudgetGroup>| null> {
    let endpoint = '';

    switch (currentRole) {
        case Role.Manager:
            endpoint = 'managerindividualbudgetvalues';
            break;
        case Role.SectionManager:
            endpoint = 'sectionmanagerindividualbudgetvalues';
            break;
        default:
            break;
    }

    if (endpoint === '') {
        return Promise.resolve(null);
    }
    const { instanceTopic } = useLoginStore();

    const promise = resgate.collection<BudgetGroup>(`v1.pay-management.budget.${instanceTopic}.${currentOrganisationId}.${salaryReviewId}.${endpoint}`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });

    return promise;
}
